import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { multibuyRate, discountedLineTotal } from "@/lib/api";

const BasketContext = createContext(null);

const STORAGE_KEY = "bsc_basket_v1";

export const BasketProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch {
            // ignore storage failures
        }
    }, [items]);

    const addItem = useCallback((product, quantity = 1, options = {}) => {
        const { openDrawer = true } = options;
        setItems((prev) => {
            const existing = prev.find((i) => i.id === product.id);
            if (existing) {
                return prev.map((i) =>
                    i.id === product.id ? { ...i, quantity: Math.min(i.quantity + quantity, 50) } : i
                );
            }
            return [
                ...prev,
                {
                    id: product.id,
                    slug: product.slug,
                    name: product.name,
                    price: product.price,
                    weight_g: product.weight_g,
                    category: product.category,
                    band_color: product.band_color,
                    quantity,
                },
            ];
        });
        toast.success(`${product.name} added to basket`, {
            description: "Traceable to the farm it came from.",
        });
        if (openDrawer) setIsOpen(true);
    }, []);

    const removeItem = useCallback((id) => {
        setItems((prev) => prev.filter((i) => i.id !== id));
    }, []);

    const updateQuantity = useCallback((id, quantity) => {
        if (quantity < 1) {
            setItems((prev) => prev.filter((i) => i.id !== id));
            return;
        }
        setItems((prev) => prev.map((i) => (i.id === id ? { ...i, quantity } : i)));
    }, []);

    const clearBasket = useCallback(() => setItems([]), []);

    const subtotal = useMemo(
        () => items.reduce((sum, i) => sum + discountedLineTotal(i), 0),
        [items]
    );
    const savings = useMemo(
        () => items.reduce((sum, i) => sum + i.price * i.quantity * multibuyRate(i.category, i.quantity), 0),
        [items]
    );
    const count = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);

    const value = {
        items,
        addItem,
        removeItem,
        updateQuantity,
        clearBasket,
        subtotal,
        savings,
        count,
        isOpen,
        setIsOpen,
    };

    return <BasketContext.Provider value={value}>{children}</BasketContext.Provider>;
};

export const useBasket = () => {
    const ctx = useContext(BasketContext);
    if (!ctx) throw new Error("useBasket must be used within BasketProvider");
    return ctx;
};
